import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI, User, UserProfileUpdate, getUserErrorMessage } from '../api/api';

const emptyProfile: UserProfileUpdate = {
  name: '',
  age: 18,
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: '',
};

const Profile: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [formData, setFormData] = useState<UserProfileUpdate>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    authAPI
      .getMe()
      .then((user: User) => {
        if (cancelled) {
          return;
        }
        setEmail(user.email);
        setEmailVerified(user.email_verified);
        setFormData({
          name: user.name,
          age: user.age,
          phone: user.phone ?? '',
          street: user.street ?? '',
          city: user.city ?? '',
          state: user.state ?? '',
          zip: user.zip ?? '',
          country: user.country ?? '',
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            getUserErrorMessage(
              err,
              'Failed to load your profile. Your session may have expired — try logging in again.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'age' ? Number(value) : value,
    }));
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (formData.age < 18 || formData.age > 120) {
      setError('Age must be between 18 and 120 years.');
      setSaving(false);
      return;
    }

    try {
      const updated = await authAPI.updateProfile(formData);
      setFormData({
        name: updated.name,
        age: updated.age,
        phone: updated.phone ?? '',
        street: updated.street ?? '',
        city: updated.city ?? '',
        state: updated.state ?? '',
        zip: updated.zip ?? '',
        country: updated.country ?? '',
      });
      setSuccess('Your profile has been updated.');
    } catch (err: unknown) {
      setError(
        getUserErrorMessage(
          err,
          'Could not save your profile. Check your name and age, then try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="container"
        style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="spinner" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="container"
      style={{ padding: '40px 16px 60px', maxWidth: '720px', margin: '0 auto' }}
    >
      <div className="card">
        <h1
          style={{
            textAlign: 'center',
            marginBottom: '8px',
            color: 'var(--neon-cyan)',
            fontSize: '2rem',
          }}
        >
          Your profile
        </h1>
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '28px' }}>
          Update your account details and mailing address.
        </p>

        {error && <div className="error">{error}</div>}
        {success && (
          <div
            className="success"
            role="status"
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(0, 255, 136, 0.12)',
              color: 'var(--neon-green, #00ff88)',
            }}
          >
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} aria-label="Update profile">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-name">
                Name
              </label>
              <input
                id="profile-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your full name"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-age">
                Age
              </label>
              <input
                id="profile-age"
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                className="form-input"
                min={18}
                max={120}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="profile-email">
              Email
            </label>
            <input
              id="profile-email"
              type="email"
              value={email}
              className="form-input"
              readOnly
              disabled
              aria-describedby="profile-email-hint"
            />
            <p id="profile-email-hint" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '6px' }}>
              Email cannot be changed here.{' '}
              {!emailVerified && (
                <>
                  <Link to="/verify-email" style={{ color: 'var(--neon-cyan)' }}>
                    Verify your email
                  </Link>{' '}
                  to unlock the full app.
                </>
              )}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="profile-phone">
              Phone
            </label>
            <input
              id="profile-phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your phone number"
            />
          </div>

          <h2
            style={{
              color: 'var(--neon-magenta)',
              margin: '30px 0 20px 0',
              textAlign: 'center',
              fontSize: '1.25rem',
            }}
          >
            Mailing address
          </h2>

          <div className="form-group">
            <label className="form-label" htmlFor="profile-street">
              Street address
            </label>
            <input
              id="profile-street"
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter street address"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-city">
                City
              </label>
              <input
                id="profile-city"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-input"
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-state">
                State
              </label>
              <input
                id="profile-state"
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="form-input"
                placeholder="State"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="profile-zip">
                ZIP
              </label>
              <input
                id="profile-zip"
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                className="form-input"
                placeholder="ZIP"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="profile-country">
              Country
            </label>
            <input
              id="profile-country"
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="form-input"
              placeholder="Country"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            style={{ width: '100%', marginTop: '20px' }}
          >
            {saving ? <div className="spinner" style={{ margin: '0 auto' }} /> : 'Save changes'}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

export default Profile;
